
import { useState, useRef } from 'react';
import { supabase } from '../../../../lib/supabase';

interface ImageUploadFormProps {
  onSuccess: () => void;
}

export default function ImageUploadForm({ onSuccess }: ImageUploadFormProps) {
  const [weekDate, setWeekDate] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    return today;
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // 최대 10개 제한
    const limitedFiles = files.slice(0, 10);
    setSelectedFiles(limitedFiles);

    // 미리보기 생성
    const newPreviews: string[] = [];
    limitedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push(e.target?.result as string);
        if (newPreviews.length === limitedFiles.length) {
          setPreviews([...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (index: number) => {
    const newFiles = [...selectedFiles];
    const newPreviews = [...previews];
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    setSelectedFiles(newFiles);
    setPreviews(newPreviews);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedFiles.length === 0) {
      alert('이미지를 선택해주세요.');
      return;
    }

    if (!weekDate) {
      alert('날짜를 선택해주세요.');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const uploadedImages: { week_date: string; image_url: string; display_order: number }[] = [];

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${i}.${fileExt}`;
        const filePath = `prayer-images/${weekDate}/${fileName}`;

        // Supabase Storage에 업로드
        const { error: uploadError } = await supabase.storage
          .from('prayer-images')
          .upload(filePath, file);

        if (uploadError) {
          // Storage 버킷이 없으면 public URL 방식 사용
          // Base64로 저장
          const reader = new FileReader();
          const base64Promise = new Promise<string>((resolve) => {
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(file);
          });
          const base64 = await base64Promise;

          uploadedImages.push({
            week_date: weekDate,
            image_url: base64,
            display_order: i,
          });
        } else {
          // 공개 URL 가져오기
          const { data: urlData } = supabase.storage
            .from('prayer-images')
            .getPublicUrl(filePath);

          uploadedImages.push({
            week_date: weekDate,
            image_url: urlData.publicUrl,
            display_order: i,
          });
        }

        setUploadProgress(Math.round(((i + 1) / selectedFiles.length) * 100));
      }

      // 데이터베이스에 저장
      const { error: dbError } = await supabase
        .from('prayer_images')
        .insert(uploadedImages);

      if (dbError) throw dbError;

      alert(`${uploadedImages.length}개의 이미지가 업로드되었습니다.`);
      
      // 폼 초기화
      setSelectedFiles([]);
      setPreviews([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      onSuccess();
    } catch (error) {
      console.error('Upload error:', error);
      alert('업로드에 실패했습니다.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* 날짜 선택 */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          주간 날짜 <span className="text-red-500">*</span>
        </label>
        <input
          type="date"
          value={weekDate}
          onChange={(e) => setWeekDate(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F4F4F] focus:border-transparent transition-all text-sm"
          required
        />
        <p className="text-xs text-gray-500 mt-1">해당 주의 수요일 날짜를 선택해주세요</p>
      </div>

      {/* 이미지 업로드 */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          기도제목 이미지 <span className="text-red-500">*</span>
          <span className="text-gray-400 font-normal ml-2">(최대 10장)</span>
        </label>
        
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[#2F4F4F] hover:bg-gray-50 transition-all cursor-pointer"
        >
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-image-add-line text-3xl text-gray-400"></i>
          </div>
          <p className="text-gray-600 font-medium">클릭하여 이미지 선택</p>
          <p className="text-sm text-gray-400 mt-1">JPG, PNG, WEBP (최대 10장)</p>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* 미리보기 */}
      {previews.length > 0 && (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            선택된 이미지 ({previews.length}장)
          </label>
          <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
            {previews.map((preview, index) => (
              <div key={index} className="relative group">
                <img
                  src={preview}
                  alt={`미리보기 ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg border border-gray-200"
                />
                <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                  {index + 1}
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 업로드 진행률 */}
      {uploading && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">업로드 중...</span>
            <span className="text-sm font-medium text-[#2F4F4F]">{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-[#2F4F4F] h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* 제출 버튼 */}
      <button
        type="submit"
        disabled={uploading || selectedFiles.length === 0}
        className="w-full bg-[#2F4F4F] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#1a3333] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
      >
        {uploading ? (
          <span className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            업로드 중...
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-upload-cloud-line"></i>
            이미지 업로드
          </span>
        )}
      </button>
    </form>
  );
}
